import {
  File,
  Download,
  Trash2,
  MoreVertical,
  Eye,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
  HardDrive,
  Calendar,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

export interface DocumentCardProps {
  id: number;
  name: string;
  status: "pending" | "uploading" | "processing" | "completed" | "error";
  size?: number;
  uploadDate?: string;
  url?: string;
  message?: string;
  onDownload: () => void;
  onDelete: () => void;
  onView?: () => void;
  onRetry?: () => void;
  selected?: boolean;
  onSelect?: (checked: boolean) => void;
  showCheckbox?: boolean;
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "—";
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(1);
  return `${size} ${sizes[i]}`;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case "pending":
      return {
        label: "Pendiente",
        variant: "secondary" as const,
        color: "text-gray-500",
        icon: File,
      };
    case "uploading":
      return {
        label: "Subiendo",
        variant: "default" as const,
        color: "text-yellow-500",
        icon: Loader2,
      };
    case "processing":
      return {
        label: "Procesando",
        variant: "default" as const,
        color: "text-yellow-500",
        icon: Loader2,
      };
    case "completed":
      return {
        label: "Completado",
        variant: "default" as const,
        color: "text-green-500",
        icon: CheckCircle2,
      };
    case "error":
      return {
        label: "Error",
        variant: "destructive" as const,
        color: "text-red-500",
        icon: AlertCircle,
      };
    default:
      return {
        label: "Desconocido",
        variant: "secondary" as const,
        color: "text-gray-500",
        icon: File,
      };
  }
};

export function DocumentCard({
  id: _id,
  name,
  status,
  size,
  uploadDate,
  url,
  message,
  onDownload,
  onDelete,
  onView,
  onRetry,
  selected = false,
  onSelect,
  showCheckbox = false,
}: DocumentCardProps) {
  const statusConfig = getStatusConfig(status);
  const StatusIcon = statusConfig.icon;

  return (
    <div
      className={cn(
        "group relative rounded-xl border border-black/5 bg-[var(--card-background)] p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-white/10",
        selected && "ring-2 ring-primary/20 border-primary/30"
      )}
    >
      {/* Checkbox para selección en masa */}
      {showCheckbox && (
        <div className="absolute top-3 left-3 z-10">
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelect?.(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
        </div>
      )}

      {/* Header con estado y acciones */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge
            variant={statusConfig.variant}
            className={cn(
              "text-xs font-medium",
              status === "processing" && "animate-pulse",
              (statusConfig.variant === "default" || statusConfig.variant === "destructive") && "text-white"
            )}
          >
            <StatusIcon className="h-3 w-3 mr-1" />
            {statusConfig.label}
          </Badge>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onView && (
            <DropdownMenuItem onClick={onView}>
              <Eye className="h-4 w-4 mr-2" />
              Ver detalles
            </DropdownMenuItem>
          )}
          {status === "error" && onRetry && (
            <DropdownMenuItem onClick={onRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </DropdownMenuItem>
          )}
            <DropdownMenuItem onClick={onDownload}>
              <Download className="h-4 w-4 mr-2" />
              Descargar
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Icono y nombre del archivo */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 p-2">
          <File className={cn("h-6 w-6", statusConfig.color)} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-[var(--foreground)] truncate leading-tight">
            {name}
          </h4>
        </div>
      </div>

      {/* Metadatos */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <HardDrive className="h-3 w-3" />
            <span>{formatFileSize(size)}</span>
          </div>
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <span>{formatDate(uploadDate)}</span>
          </div>
        </div>
      </div>

      {/* Barra de progreso para archivos en proceso */}
      {(status === "uploading" || status === "processing") && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Progreso</span>
            <span className="font-medium">
              {status === "uploading" ? "Subiendo..." : "Procesando..."}
            </span>
          </div>
          <Progress value={status === "uploading" ? 30 : 70} className="h-1.5" />
        </div>
      )}

      {/* Mensaje de estado */}
      {message && (
        <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-black/5">
          {message}
        </div>
      )}

      {/* Acción principal (descargar) */}
      {url && status === "completed" && (
        <div className="mt-3 pt-3 border-t border-black/5">
          <Button
            onClick={onDownload}
            size="sm"
            className="w-full"
            variant="outline"
          >
            <Download className="h-4 w-4 mr-2" />
            Descargar archivo
          </Button>
        </div>
      )}
    </div>
  );
}